/**
 * Loads the third-party tracking vendors, but only after the user grants the "Tracking" category.
 *
 * This is the code-level half of prior blocking (brief R1): every vendor here makes its first
 * network request when its SDK initializes, so none of them may run before the user chooses. The
 * consent manager's own network-level blocker is the backstop for anything that slips past here.
 *
 * Google is the one exception, granted by APP-1182: GA4 may run before the choice, but only in a
 * cookieless state — no cookie, no client id, and no application event parameters either, because
 * APP-1182 forbids sending user ids or custom dimensions before consent.
 *
 * Teardown on revocation is handled by a full page reload (c15t's onBeforeConsentRevocationReload):
 * after revoking, the page reloads with Tracking denied and nothing here runs.
 */

import * as Sentry from "@sentry/react";

import { ADSENSE_CLIENT, ADS_ENABLED } from "~/lib/ads";
import { NOTICE_VERSION } from "~/lib/consent";
import { isDevelopment, isProduction } from "~/lib/env";
import { trackWebEvent } from "~/lib/web-analytics";

// A one-shot latch, kept separate from the consent decision in `tracking-consent`. Conflating them
// was a bug: seeding this from c15t would have made the guard below skip every vendor.
let vendorsLoaded = false;

export function loadTrackingVendors() {
  if (vendorsLoaded || typeof window === "undefined") {
    return;
  }
  vendorsLoaded = true;

  // Counsel requires the consent log to show when non-essential tracking was first permitted to
  // activate, paired with consent_prompt_shown: cases have turned on gaps of seconds between the
  // two. Emitted before the vendors themselves so the timestamp is the permission, not the network.
  // One row per page load, not one per user: this function is idempotent only within a page load, and
  // a consented visitor runs it again on every load. What counsel asked for is the *first* activation,
  // which is min(event_time) at query time — so count with min, not with a row count.
  trackWebEvent({ event_type: "consent_tracking_activated", notice_version: NOTICE_VERSION });

  // Flip the Consent Mode v2 signals to granted. GTM is already loaded; tags that were held back
  // re-evaluate against the new state, and GA4 leaves cookieless mode. `ad_user_data` and
  // `ad_personalization` are the v2 additions Google requires to retain Ads remarketing/audiences
  // for EEA/UK traffic.
  // An inline script defines `gtag`, and a strict CSP or a privacy extension can strip it. Without
  // this guard an unhandled TypeError would abort the rest of this function,
  // silently costing a consenting user their ads and session replay for the sake of a signal that
  // could not have been delivered anyway.
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: "granted",
      analytics_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
    });
  }

  // AdSense has no cookieless mode — a non-personalized ad still sets cookies — so unlike GA4 it
  // gets no pre-consent exemption and simply does not load until here. That also removes the need
  // for `requestNonPersonalizedAds`: the loader only ever starts with consent already granted.
  // Production only: nowhere else is an approved AdSense origin, so a loader would add network and
  // cookies with nothing to fill. Behind the kill switch, the only way to stop serving.
  if (isProduction && ADS_ENABLED) {
    const adsense = document.createElement("script");
    adsense.async = true;
    adsense.crossOrigin = "anonymous";
    adsense.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
    document.head.appendChild(adsense);
  }

  // Sentry Session Replay records the user's screen (PII), so it is added only under Tracking
  // consent. Basic error monitoring is essential and stays always-on.
  if (!isDevelopment) {
    Sentry.addIntegration(Sentry.replayIntegration());
  }
}
